import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiChevronDown } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    {
      name: 'Services',
      path: '#',
      hasDropdown: true,
      subItems: [
        { name: 'Assignment Support Services', path: '/assignment-support' },
        { name: 'Tutoring Services', path: '/tutoring-services' },
        { name: 'Online Exam Help US', path: '/online-exam-help' }
      ]
    },
    { name: 'Contact Us', path: '/contact' }
  ];

  return (
    <header className={`sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? 'shadow-lg' : 'shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          <Link to="/" className="flex-shrink-0">
            {/* Light logo for header */}
            <img
              src="https://i.ibb.co/39dmWcR7/Tutorhub-Logo-light.png"
              alt="Tutor Hub Online"
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.hasDropdown ? (
                  <div className="relative group">
                    <button className="flex items-center space-x-1 text-gray-700 hover:text-primary transition-colors py-2">
                      <span>{link.name}</span>
                      <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                    </button>
                    {/* Dropdown menu with proper hover area */}
                    <div className="absolute top-full left-0 mt-0 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-in-out">
                      <div className="pt-2">
                        <div className="bg-white rounded-lg shadow-lg border py-2">
                          {link.subItems.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.path}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  link.path === '/' ? (
                    <Link
                      to={link.path}
                      className={`text-gray-700 hover:text-primary transition-colors ${
                        location.pathname === link.path ? 'text-primary font-medium' : ''
                      }`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-gray-700 hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  )
                )}
              </div>
            ))}
          </nav>

          {/* ✅ DESKTOP BUTTONS - Both buttons are here */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/become-tutor"
              className="border-2 border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary hover:text-white transition-colors font-medium"
            >
              Become a Tutor
            </Link>
            <Link
              to="/book-tutor"
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Book an Online Tutor
            </Link>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary transition-colors"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* ✅ MOBILE MENU - Both buttons are here too */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t">
          <div className="px-4 py-2 space-y-1">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.hasDropdown ? (
                  <div>
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      className="flex items-center justify-between w-full px-3 py-2 text-gray-700 hover:text-primary transition-colors"
                    >
                      <span>{link.name}</span>
                      <SafeIcon
                        icon={FiChevronDown}
                        className={`w-4 h-4 transition-transform ${
                          isServicesOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {isServicesOpen && (
                      <div className="pl-4">
                        {link.subItems.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            className="block px-3 py-2 text-sm text-gray-600 hover:text-primary transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  link.path === '/' ? (
                    <Link
                      to={link.path}
                      className={`block px-3 py-2 text-gray-700 hover:text-primary transition-colors ${
                        location.pathname === link.path ? 'text-primary font-medium' : ''
                      }`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <Link
                      to={link.path}
                      className="block px-3 py-2 text-gray-700 hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  )
                )}
              </div>
            ))}
            {/* ✅ MOBILE BUTTONS SECTION */}
            <div className="pt-4 border-t space-y-2">
              <Link
                to="/become-tutor"
                className="block border-2 border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary hover:text-white transition-colors font-medium text-center"
              >
                Become a Tutor
              </Link>
              <Link
                to="/book-tutor"
                className="block bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-center"
              >
                Book an Online Tutor
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;